import React from "react";

export const HELP_CHAT_MAX_FILES = 5;
export const HELP_CHAT_MAX_FILE_MB = 50;
export const HELP_CHAT_OPEN_EVENT = "help-chat:open";

export const HELP_CHAT_SUGGESTIONS = [
  "Which submittals are late?",
  "Which RFIs are in my court?",
  "How do I create an RFI?",
  "Create an RFI",
  "PDF report of open RFIs",
];

export const HELP_CHAT_TOOL_LABELS = {
  list_projects: "Projects",
  get_project_summary: "Project summary",
  search_submittals: "Submittals",
  get_submittal: "Submittals",
  search_rfis: "RFIs",
  get_rfi: "RFIs",
  get_daily_reports: "Daily reports",
  search_drawings: "Drawings",
  list_documents: "Documents",
  search_tasks: "Tasks",
  get_task: "Tasks",
  search_punch_list_items: "Punch lists",
  get_punch_list_item: "Punch lists",
  search_contracts: "Contracts",
  search_change_orders: "Change orders",
  get_safety_inspections: "Safety",
  preview_submittal: "Submittals",
  create_submittal: "Submittals",
  preview_rfi: "RFIs",
  create_rfi: "RFIs",
  preview_task: "Tasks",
  create_task: "Tasks",
  preview_punch_list_item: "Punch lists",
  create_punch_list_item: "Punch lists",
  preview_daily_report: "Daily reports",
  create_daily_report: "Daily reports",
  prepare_pdf_report: null,
  read_uploaded_file: null,
};

export const HELP_CHAT_CREATE_QUERY_KEYS = {
  create_submittal: ["submittals"],
  create_rfi: ["rfis"],
  create_task: ["tasks", "task"],
  create_punch_list_item: ["punch-list-items", "punch-lists"],
  create_daily_report: ["daily-report", "daily-reports-range"],
};

export const helpChatSourceLabel = (toolsUsed) => {
  const labels = [
    ...new Set(
      (toolsUsed || [])
        .map((tool) =>
          tool in HELP_CHAT_TOOL_LABELS ? HELP_CHAT_TOOL_LABELS[tool] : tool
        )
        .filter(Boolean)
    ),
  ];
  return labels.length ? `Live project data: ${labels.join(", ")}` : "";
};

const HELP_CHAT_DEEP_LINK_PARAMS = {
  "/submittals": "submittal_id",
  "/rfis": "rfi_id",
  "/tasks": "task_id",
  "/daily-reports": "date",
};

export const helpChatInternalPath = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    const deepLinkParam =
      HELP_CHAT_DEEP_LINK_PARAMS[parsed.pathname] ||
      (parsed.pathname.startsWith("/punch-lists/") ? "punch_list_item_id" : null);
    const isAppLink =
      parsed.origin === window.location.origin ||
      Boolean(deepLinkParam && parsed.searchParams.has(deepLinkParam));
    return isAppLink ? `${parsed.pathname}${parsed.search}` : null;
  } catch {
    return null;
  }
};

/** Matches App.js RoleProtectedRoute. Unlisted routes are open to every role. */
export const HELP_CHAT_ROUTE_ROLES = {
  "/spec-sections": ["owner", "project_manager"],
  "/vendors": ["owner", "project_manager"],
  "/users": ["owner", "project_manager"],
  "/schedule": ["owner", "project_manager", "field"],
  "/contracts": ["owner", "project_manager"],
  "/daily-reports": ["owner", "project_manager", "field"],
  "/safety": ["owner", "project_manager", "field"],
  "/workers": ["owner", "project_manager"],
};

export const canOpenHelpChatRoute = (route, role) => {
  const allowed = HELP_CHAT_ROUTE_ROLES[route];
  if (!allowed) return true;
  return Boolean(role && allowed.includes(role));
};

export const HELP_CHAT_ROUTE_LABELS = {
  "/": "Home",
  "/rfis": "RFIs",
  "/submittals": "Submittals",
  "/shop-drawings": "Shop Drawings",
  "/tasks": "Tasks",
  "/drawing-folders": "Drawings",
  "/drawings": "Drawings",
  "/documents": "Documents",
  "/daily-reports": "Daily Reports",
  "/punch-lists": "Punch Lists",
  "/change-orders": "Change Orders",
  "/contracts": "Contracts",
  "/schedule": "Schedule",
  "/workers": "AI Workers",
  "/vendors": "Vendors",
  "/spec-sections": "Spec Sections",
  "/users": "Users",
  "/settings": "Settings",
};

export const helpChatRouteLabel = (route) =>
  HELP_CHAT_ROUTE_LABELS[route] ||
  String(route || "")
    .replace(/^\//, "")
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

export const helpChatModuleLabel = (pathname) => {
  const route = `/${String(pathname || "").split("/")[1] || ""}`;
  return route === "/" ? undefined : HELP_CHAT_ROUTE_LABELS[route];
};

const HELP_CHAT_INLINE_RE =
  /(\*\*[^*]+\*\*|\[[^\]]+\]\(https?:\/\/[^\s)]+\)|https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"]|`[^`]+`)/g;
const HELP_CHAT_TABLE_DIVIDER_RE =
  /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;
const HELP_CHAT_HEADING_RE = /^#{1,6}\s+(.*)$/;
const HELP_CHAT_LIST_RE = /^\s*(?:[-*•]|(\d+)[.)])\s+(.*)$/;

const isHelpChatTableRow = (line) => Boolean(line.trim()) && line.includes("|");

const isHelpChatTableStart = (line, nextLine = "") =>
  isHelpChatTableRow(line) &&
  nextLine.includes("|") &&
  HELP_CHAT_TABLE_DIVIDER_RE.test(nextLine);

export const helpChatHasTable = (text) =>
  String(text || "")
    .split(/\r?\n/)
    .some((line, i, lines) => isHelpChatTableStart(line, lines[i + 1]));

const renderHelpChatInline = (text, key, onLinkClick) =>
  String(text)
    .split(HELP_CHAT_INLINE_RE)
    .filter(Boolean)
    .map((part, i) => {
      const partKey = `${key}-${i}`;
      const bold = part.match(/^\*\*([^*]+)\*\*$/);
      if (bold) {
        return (
          <strong key={partKey} className="font-semibold">
            {renderHelpChatInline(bold[1], partKey, onLinkClick)}
          </strong>
        );
      }
      const link = part.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
      if (link || /^https?:\/\//.test(part)) {
        const href = link ? link[2] : part;
        return (
          <a
            key={partKey}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => onLinkClick?.(event, href)}
            className="break-words font-medium text-[#1677ff] underline"
          >
            {link ? link[1] : part}
          </a>
        );
      }
      const code = part.match(/^`([^`]+)`$/);
      if (code) {
        return (
          <code key={partKey} className="rounded bg-gray-200 px-1 text-[12px]">
            {code[1]}
          </code>
        );
      }
      return <React.Fragment key={partKey}>{part}</React.Fragment>;
    });

const HELP_CHAT_ESCAPED_PIPE = "";

const splitHelpChatTableRow = (line) =>
  line
    .replace(/\\\|/g, HELP_CHAT_ESCAPED_PIPE)
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.split(HELP_CHAT_ESCAPED_PIPE).join("|").trim());

const parseHelpChatBlocks = (text) => {
  const lines = String(text).split(/\r?\n/);
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }
    if (isHelpChatTableStart(line, lines[i + 1])) {
      const header = splitHelpChatTableRow(line);
      const rows = [];
      i += 2;
      while (i < lines.length && isHelpChatTableRow(lines[i])) {
        rows.push(splitHelpChatTableRow(lines[i]));
        i += 1;
      }
      blocks.push({ type: "table", header, rows });
      continue;
    }
    const heading = line.match(HELP_CHAT_HEADING_RE);
    if (heading) {
      blocks.push({ type: "heading", text: heading[1] });
      i += 1;
      continue;
    }
    const listItem = line.match(HELP_CHAT_LIST_RE);
    if (listItem) {
      const ordered = Boolean(listItem[1]);
      const items = [];
      while (i < lines.length) {
        const match = lines[i].match(HELP_CHAT_LIST_RE);
        if (!match || Boolean(match[1]) !== ordered) break;
        items.push(match[2]);
        i += 1;
      }
      blocks.push({
        type: ordered ? "ol" : "ul",
        items,
        start: ordered ? Number(listItem[1]) : undefined,
      });
      continue;
    }
    const paragraph = [line];
    i += 1;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !isHelpChatTableStart(lines[i], lines[i + 1]) &&
      !HELP_CHAT_HEADING_RE.test(lines[i]) &&
      !HELP_CHAT_LIST_RE.test(lines[i])
    ) {
      paragraph.push(lines[i]);
      i += 1;
    }
    blocks.push({ type: "p", lines: paragraph });
  }
  return blocks;
};

/** Render the assistant's Markdown (tables, lists, headings, **bold**, links) as React nodes; never injects HTML. */
export const formatHelpChatText = (text, onLinkClick) => {
  if (!text) return null;
  return parseHelpChatBlocks(text).map((block, b) => {
    const key = `b${b}`;
    if (block.type === "table") {
      return (
        <div
          key={key}
          className="my-2 overflow-x-auto rounded-lg border border-gray-200 bg-white"
        >
          <table className="min-w-full border-collapse text-left text-xs">
            <thead className="bg-gray-50">
              <tr>
                {block.header.map((cell, c) => (
                  <th
                    key={c}
                    className="whitespace-nowrap border-b border-gray-200 px-2.5 py-2 font-semibold text-gray-600"
                  >
                    {renderHelpChatInline(cell, `${key}-h${c}`, onLinkClick)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr
                  key={r}
                  className="border-b border-gray-100 last:border-0 hover:bg-[#f5f7fa]"
                >
                  {block.header.map((_, c) => (
                    <td key={c} className="px-2.5 py-1.5 align-top text-gray-800">
                      {renderHelpChatInline(
                        row[c] || "",
                        `${key}-${r}-${c}`,
                        onLinkClick
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    if (block.type === "heading") {
      return (
        <div key={key} className="mb-1 mt-2 font-semibold first:mt-0">
          {renderHelpChatInline(block.text, key, onLinkClick)}
        </div>
      );
    }
    if (block.type === "ul" || block.type === "ol") {
      const ListTag = block.type;
      return (
        <ListTag
          key={key}
          start={block.start}
          className={`my-1 space-y-0.5 pl-5 ${
            block.type === "ol" ? "list-decimal" : "list-disc"
          }`}
        >
          {block.items.map((item, n) => (
            <li key={n}>
              {renderHelpChatInline(item, `${key}-${n}`, onLinkClick)}
            </li>
          ))}
        </ListTag>
      );
    }
    return (
      <p key={key} className="my-1 first:mt-0 last:mb-0">
        {block.lines.map((line, n) => (
          <React.Fragment key={n}>
            {n > 0 && <br />}
            {renderHelpChatInline(line, `${key}-${n}`, onLinkClick)}
          </React.Fragment>
        ))}
      </p>
    );
  });
};
